import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Eye, Heart, Users } from 'lucide-react';

export const VisionBlock = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const features = [
    {
      icon: Eye,
      title: 'גלו עסקים סביבכם',
      description: 'מהאופה השכן ועד התופרת המקומית',
    },
    {
      icon: Heart,
      title: 'תמכו בקהילה שלכם',
      description: 'כל קנייה מקומית מחזקת את השכונה',
    },
    {
      icon: Users,
      title: 'התחברו לאנשים',
      description: 'הכירו את השכנים והעסקים הקרובים אליכם',
    },
  ];

  return (
    <section ref={ref} className="relative py-12 sm:py-16 px-6 bg-white overflow-hidden">
      {/* Subtle background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-brand-400/10 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-secondary-500/10 to-transparent rounded-full blur-3xl" />

      <div className="relative max-w-6xl mx-auto">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-10"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-l from-brand-700 via-brand-600 to-brand-700 leading-tight mb-3">
            החזון שלנו הוא שתכירו
            <br />
            את מי שסביבכם ותצרכו מקומי
          </h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base sm:text-lg md:text-xl text-brand-700 font-medium"
          >
            בונים ביחד קהילה חזקה ומקומית
          </motion.p>
        </motion.div>

        {/* Feature cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.15 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="relative group"
            >
              <div className="relative bg-gradient-to-br from-white to-surface rounded-3xl p-4 sm:p-5 md:p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-brand-400/10 h-full">
                {/* Hover glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-brand-400/5 to-secondary-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className="relative">
                  {/* Icon */}
                  <div className="mb-4 sm:mb-5 md:mb-6 inline-block">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-brand-400 to-brand-500 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300" />
                      <div className="relative bg-gradient-to-br from-brand-400 to-brand-500 p-3 sm:p-3.5 md:p-4 rounded-2xl">
                        <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" strokeWidth={1.5} />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl sm:text-xl md:text-2xl font-bold text-brand-700 mb-2 sm:mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-base sm:text-base md:text-lg text-brand-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};
